import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParameterCodec } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ObjectUtils } from '@igo2/utils';

import { FEATURE, Feature } from '@igo2/geo';

import { SearchResult } from '@igo2/geo';
import { SearchSource, TextSearch } from '@igo2/geo';
import {
  SearchSourceOptions,
  TextSearchOptions
} from '@igo2/geo';

import * as olformat from 'ol/format';

import { computeTermSimilarity } from '@igo2/geo';
import {
  WfsSearchSourceOptions,
  WfsData,
  WfsResponse
} from './wfs.interfaces';
import { LanguageService } from '@igo2/core/language';
import { StorageService } from '@igo2/core/storage';
//TODO check if we need this import { Cacheable } from 'ts-cacheable';

@Injectable()
export class WfsSearchResultFormatter {
  constructor(private languageService: LanguageService) { }

  formatResult(result: SearchResult<Feature>): SearchResult<Feature> {
    return result;
  }
}

// Fix the "+" is replaced with space " " in a query string
// https://github.com/angular/angular/issues/11058
export class IgoHttpParameterCodec implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

/**
 * Wfs search source
 */

@Injectable()
export class WfsSearchSource extends SearchSource
  implements TextSearch {
  static propertiesBlacklist: string[] = [
    'boundedBy',
    'id',
    'coord_x',
    'coord_y'
  ];
  public WfsOptions: WfsSearchSourceOptions;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    storageService: StorageService,
    @Inject('options') options: SearchSourceOptions
  ) {
    super(options, storageService);

    this.WfsOptions = options as WfsSearchSourceOptions;
    if (this.WfsOptions && !this.WfsOptions.available) {
      return;
    }
  }

  getId(): string {
    return WfsSearchSource.id;
  }

  getType(): string {
    return WfsSearchSource.type;
  }

  getEffectiveOptions(): SearchSourceOptions {
    return {
      title: 'Localisation Wfs'
    };
  }

  /**
   * Search a location by name or keyword
   * @param term Location name or keyword
   * @returns Observable of <SearchResult<Feature>[]
   */
  //@Cacheable({
  //  maxCacheCount: 20
  //})
  search(
    term: string,
    options?: TextSearchOptions
  ): Observable<SearchResult<Feature>[]> {

    this.options.params = this.options.params ? this.options.params : {};
    this.options.params.page = options.page ? String(options.page) : '1';

    let url = this.WfsOptions["searchUrl"];
    let nomCouche = this.WfsOptions["nomCoucheWfs"];
    let champsDeRecherche = this.WfsOptions["champsDeRecherche"];
    let srsname = this.WfsOptions["srsname"];
    let p1 = this.WfsOptions["p1"];

    let critere = term.replace("[^A-Za-zÀ-ÿ0-9]+", " ");
    let arrMots = critere.split(' ');
    let critereFormate = arrMots.join("*");
    let limit = this.options.params.limit;

    let sbRequeteWFS = "<?xml version=\"1.0\" encoding=\"UTF-8\"?>";
    sbRequeteWFS = sbRequeteWFS + "<wfs:GetFeature xmlns:wfs=\"http://www.opengis.net/wfs\" xmlns:p1=\"" + p1 + "\" version=\"1.1.0\" service=\"WFS\" maxFeatures=\"" + limit + "\">";
    sbRequeteWFS = sbRequeteWFS + "   <wfs:Query typeName=\"" + nomCouche + "\" srsName=\"" + srsname + "\">";
    sbRequeteWFS = sbRequeteWFS + "      <Filter xmlns=\"http://www.opengis.net/ogc\">";
    sbRequeteWFS = sbRequeteWFS + "           <Or>";

    champsDeRecherche.forEach(function (itemChampRecherche) {
      sbRequeteWFS = sbRequeteWFS + "            <PropertyIsLike wildCard=\"*\" singleChar=\"?\" escapeChar=\"!\" matchCase=\"false\">";
      sbRequeteWFS = sbRequeteWFS + "               <vrp1:PropertyName xmlns:vrp1=\"http://www.opengis.net/ogc\" xmlns=\"" + url + "\">" + itemChampRecherche + "</vrp1:PropertyName>";
      sbRequeteWFS = sbRequeteWFS + "            <Literal>*" + critereFormate + "*</Literal>";
      sbRequeteWFS = sbRequeteWFS + "            </PropertyIsLike>";
    });

    sbRequeteWFS = sbRequeteWFS + "           </Or>";
    sbRequeteWFS = sbRequeteWFS + "      </Filter>";
    sbRequeteWFS = sbRequeteWFS + "   </wfs:Query>";
    sbRequeteWFS = sbRequeteWFS + "</wfs:GetFeature>";

    //console.log(sbRequeteWFS);

    return this.http.post(url, sbRequeteWFS).pipe(
      map(response => {
        let resultArray = this.extractResults(this.extractWFSData(response), term);
        resultArray.sort((a, b) =>
          (a.meta.score > b.meta.score) ? 1 :
            (a.meta.score === b.meta.score) ? ((a.meta.titleHtml < b.meta.titleHtml) ? 1 : -1) : -1);
        resultArray.reverse();
        if (resultArray.length > Number(this.options.params.limit)) {
          const idxEnd = Number(this.options.params.limit) * Number(this.options.params.page);
          const resultTotLenght = resultArray.length;
          resultArray = resultArray.slice(0, idxEnd);
          if (idxEnd < resultTotLenght) {
            resultArray[resultArray.length - 1].meta.nextPage = true;
          } else {
            resultArray[resultArray.length - 1].meta.nextPage = false;
          }
        }
        return resultArray;
      })
    );
  }

  private getFormatFromOptions() {
    let olFormatCls;

    const outputFormat = this.WfsOptions.outputformat;
    const patternGml3 = new RegExp('.*?gml.*?', 'i');
    const patternGeojson = new RegExp('.*?json.*?', 'i');

    if (patternGeojson.test(outputFormat)) {
      olFormatCls = olformat.GeoJSON;
    }
    if (patternGml3.test(outputFormat)) {
      olFormatCls = olformat.WFS;
    }

    return new olFormatCls();
  }

  private extractWFSData(res) {
    const olFormat = this.getFormatFromOptions();
    const geojson = olformat.GeoJSON;
    const wfsfeatures = olFormat.readFeatures(res);
    const features = JSON.parse(new geojson().writeFeatures(wfsfeatures));
    return features;
  }

  private extractResults(
    response: WfsResponse, term: string
  ): SearchResult<Feature>[] {
    return response.features.map((data: WfsData) => {
      return this.dataToResult(data, term);
    });
  }

  private dataToResult(data: WfsData, term: string): SearchResult<Feature> {
    const properties = this.computeProperties(data);
    const id = [this.getId(), properties.type, data.id].join('.');

    let champsLebelle = this.WfsOptions["libelles"];
    var libelle = "";
    champsLebelle.forEach(function (itemlibelle) {
      var valeur = itemlibelle;
      if (itemlibelle.includes('@')) {
        valeur = data.properties[itemlibelle.replace('@', ' ').trim()];
      }
      libelle += valeur;
    });

    return {
      source: this,
      data: {
        type: FEATURE,
        projection: 'EPSG:3857',
        geometry: data.geometry,
        // extent: data.bbox,
        properties,
        meta: {
          id,
          title: libelle
        }
      },
      meta: {
        dataType: FEATURE,
        id,
        title: libelle,
        titleHtml: libelle,
        icon: 'map-marker',
        score: (libelle) ?
          computeTermSimilarity(term.trim(), libelle) :
          computeTermSimilarity(term.trim(), libelle),

      }
    };
  }

  private computeProperties(data: WfsData): { [key: string]: any } {
    const properties = Object.assign(
      {},
      ObjectUtils.removeKeys(
        data.properties,
        WfsSearchSource.propertiesBlacklist
      ),
      { Route: '<span class="routing"> <u>' + this.languageService.translate.instant('igo.geo.seeRouting') + '</u> </span>' }
    );
    return properties;
  }
}
